import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import GeoResponsiveList from "../../common/Components/GeoList/GeoResponsiveList";
import notifications from "../../common/Components/Notifications/Notification";
import { canManageKarkhana } from "../../helpers/karkhanaEnabled";
import {
  createSupervisor,
  listStudioManagers,
  listSupervisors,
  makeSupervisorWorker,
  setSupervisorAccess,
  setSupervisorPin,
  signOutSupervisorPhones,
  unlinkSupervisorWorker,
} from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import "./workers/karkhana-workers.scss";
import PageHeader, { HeaderButton } from "./ui/PageHeader";
import { confirmDialog } from "./ui/dialog";
import { loginMessage, makePin, smsHref, supervisorLoginUrl, whatsappHref } from "./supervisor/supervisorLink";
import "./supervisor/supervisor-login.scss";
import { SupervisorEditSheet, SupervisorMapSheet, SupervisorRow, serverText, workerLabel } from "./supervisor/SupervisorSheets";
import { roundSummary, SupervisorRoundSheet } from "./supervisor/SupervisorRoundSheet";
import { readLang, translate, useKT } from "./i18n/kt";
import { kp } from "./karkhanaPaths";

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

const call = async (fn: () => Promise<any>, ok: string) => {
  try {
    const res: any = await fn();
    if (!res?.success) {
      notifications({ title: "Error", message: serverText(res, (s) => translate(readLang(), s)), type: "danger" });
      return null;
    }
    notifications({ title: "Done", message: ok, type: "success" });
    return res.data;
  } catch {
    notifications({ title: "Error", message: "Could not reach the server", type: "danger" });
    return null;
  }
};

const readStudioName = (): string => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.organizationResponse?.name || "";
  } catch {
    return "";
  }
};

const KarkhanaSupervisorsPage = () => {
  const history = useHistory();
  const role = readRole();
  const isAdmin = role === "ORGANIZATION_ADMIN";
  const [rows, setRows] = useState<any[] | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", pin: "", alsoWorker: false });
  // What to send a supervisor: the private link, their mobile and (right after add / new PIN) the PIN.
  const [shown, setShown] = useState<{ name: string; phone: string; pin?: string } | null>(null);
  const studio: string = readStudioName();
  const loginUrl = supervisorLoginUrl(window.location.origin, studio);
  const [busy, setBusy] = useState(false);
  const { t } = useKT();
  const [editing, setEditing] = useState<SupervisorRow | null>(null);
  const [mapping, setMapping] = useState<SupervisorRow | null>(null);
  const [roundFor, setRoundFor] = useState<SupervisorRow | null>(null);
  // Managers can be paid as workers too; null while loading, "error" when the list didn't come.
  const [managers, setManagers] = useState<any[] | null | "error">(null);

  const loadManagers = useCallback(async () => {
    try {
      const res: any = await listStudioManagers();
      setManagers(res?.success ? res.data || [] : "error");
    } catch {
      setManagers("error");
    }
  }, []);

  const load = useCallback(async () => {
    loadManagers();
    try {
      const res: any = await listSupervisors();
      setRows(res?.success ? res.data || [] : []);
    } catch {
      setRows([]);
    }
  }, [loadManagers]);

  useEffect(() => {
    if (!canManageKarkhana(role)) {
      history.replace(kp.home());
      return;
    }
    if (isAdmin) load();
  }, [role, isAdmin, history, load]);

  const add = async () => {
    setBusy(true);
    try {
      const data = await call(() => createSupervisor(form), `${form.name} can now log in`);
      if (data) {
        setOpen(false);
        setShown({ name: data.name, phone: data.phone, pin: form.pin });
        // The login is made, but they couldn't be added to pay yet: say why; "Also works here" retries later.
        if (data.workerError) {
          notifications({ title: `${data.name} isn't on pay yet`, message: data.workerError, type: "warning" });
        }
        load();
      }
    } finally {
      setBusy(false);
    }
  };

  const newPin = async (s: any) => {
    if (!(await confirmDialog({ title: `Give ${s.name} a new PIN? The old PIN stops working and their phone signs out.` }))) return;
    const pin = makePin();
    if (await call(() => setSupervisorPin(s.id, pin), "PIN changed")) {
      setShown({ name: s.name, phone: s.phone, pin });
      load();
    }
  };

  const signOutPhones = async (s: any) => {
    if (!(await confirmDialog({ title: `Sign ${s.name} out on every phone? They sign in again with their PIN.` }))) return;
    if (await call(() => signOutSupervisorPhones(s.id), "Signed out on every phone")) load();
  };

  const toggle = async (s: any) => {
    if (s.active && !(await confirmDialog({ title: `Turn off ${s.name}'s access? They are signed out within a minute.` }))) return;
    if (await call(() => setSupervisorAccess(s.id, !s.active), s.active ? "Access turned off" : "Access turned on")) load();
  };

  const alsoWorks = async (s: any) => {
    if (
      !(await confirmDialog({
        title: `Does ${s.name} also work here and get paid?`,
        ok: "Yes, add to pay",
        body: "They join attendance and the pay sheet as an office worker, paid by the hour. Another supervisor or you mark their attendance; they can't mark their own.",
      }))
    )
      return;
    if (await call(() => makeSupervisorWorker(s.id), `${s.name} is on attendance and pay now`)) load();
  };

  const unlink = async (s: any) => {
    if (
      !(await confirmDialog({
        title: t("Unmap {{worker}} from {{name}}?", { worker: workerLabel(s.worker), name: s.name }),
        body: t("The worker, their attendance and pay stay. {{name}} keeps their login.", { name: s.name }),
        ok: t("Unmap"),
      }))
    )
      return;
    if (await call(() => unlinkSupervisorWorker(s.id), t("Worker unmapped"))) load();
  };

  // After a write, close the sheet and reload the list (rows carry the mapping and suggestions).
  const saved = () => {
    setEditing(null);
    setMapping(null);
    setRoundFor(null);
    load();
  };

  // Paid here as a worker: the chip and Unmap, or Not mapped (supervisors can also Map to an existing worker).
  const workerCell = (s: any, canMap: boolean) =>
    s.worker ? (
      <span className="kw-chips ksv-worker">
        <span className="kw-chip is-ok" title={t("Paid here as this worker")}>
          {workerLabel(s.worker)}
        </span>
        <button type="button" className="ksv-link" onClick={() => unlink(s)}>
          {t("Unmap")}
        </button>
      </span>
    ) : (
      <span className="kw-chips ksv-worker">
        <span className="kw-chip is-review">{t("Not mapped")}</span>
        {canMap ? (
          <button type="button" className="ksv-link" onClick={() => setMapping(s)}>
            {t("Map to worker")}
          </button>
        ) : null}
        {canMap && s.suggestions?.length ? (
          <span className="kw-muted ksv-hint">{t("Maybe {{worker}}", { worker: workerLabel(s.suggestions[0]) })}</span>
        ) : null}
      </span>
    );

  if (!canManageKarkhana(role)) return null;

  return (
    <div className="gi-registry-page kw-page">
      <PageHeader title="Supervisors" back={kp.settings()}>
        {isAdmin ? (
          <HeaderButton
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(loginUrl);
                notifications({ title: "Copied", message: "Supervisor login link copied", type: "success" });
              } catch {
                notifications({ title: "Login link", message: loginUrl, type: "info" });
              }
            }}
          >
            Copy login link
          </HeaderButton>
        ) : null}
        {isAdmin ? (
          <HeaderButton
            primary
            add
            onClick={() => {
              setForm({ name: "", phone: "", email: "", pin: makePin(), alsoWorker: false });
              setOpen(true);
            }}
          >
            Add supervisor
          </HeaderButton>
        ) : null}
      </PageHeader>
      <p className="kw-lede">
        Supervisors sign in at their own link — <span className="kw-code">{decodeURIComponent(loginUrl.replace(/^https?:\/\//, "").split("?")[0])}</span> — which
        isn't on the public site; send it with their 6-digit PIN on WhatsApp or SMS. They sign in once with their phone number and PIN and stay signed in on that
        phone (they can add Karkhana to the home screen like an app). They see the shop floor only: rounds (looms,
        sarees, inches, closures), attendance, job work, late-entry approvals, and paying people from the cash you hand them (pay, pay sheets, cash book). They can't
        open products, rate cards, costing or any other part of HKP. Cash they pay out comes off what they hold in the cash book.
      </p>
      {!isAdmin ? (
        <div className="kw-empty">Only the studio admin manages supervisors.</div>
      ) : (
        <GeoResponsiveList
          rows={rows || []}
          rowKey={(s) => s.id}
          tableClassName="ksv-table"
          empty={rows === null ? "Loading…" : "No supervisors yet"}
          actions={(s) => (
            <span className="kw-toolbar" style={{ margin: 0 }}>
              <Button size="sm" outline onClick={() => setEditing(s)}>
                {t("Edit")}
              </Button>
              <Button size="sm" outline onClick={() => setShown({ name: s.name, phone: s.phone })}>
                Share link
              </Button>
              <Button size="sm" outline onClick={() => newPin(s)}>
                {s.hasPin ? "New PIN" : "Give PIN"}
              </Button>
              {s.phonesSignedIn > 0 ? (
                <Button size="sm" outline onClick={() => signOutPhones(s)}>
                  Sign out phones
                </Button>
              ) : null}
              <Button size="sm" outline onClick={() => toggle(s)}>
                {s.active ? "Turn off" : "Turn on"}
              </Button>
              {!s.worker ? (
                <Button size="sm" outline onClick={() => alsoWorks(s)}>
                  Also works here
                </Button>
              ) : null}
            </span>
          )}
          columns={[
            { key: "name", header: "Name", cell: (s) => s.name },
            { key: "phone", header: "Logs in with", cell: (s) => <span className="kw-code">{s.phone}</span> },
            { key: "email", header: "Email", hideSm: true, cell: (s) => s.email || "—" },
            { key: "phones", header: "Signed in on", hideSm: true, cell: (s) => (s.phonesSignedIn === 1 ? "1 phone" : `${s.phonesSignedIn || 0} phones`) },
            {
              key: "access",
              header: "Access",
              // Access and PIN together, so a phone shows as many columns as before.
              cell: (s) => (
                <span className="kw-chips">
                  <span className={`kw-chip ${s.active ? "is-ok" : ""}`}>{s.active ? "On" : "Off"}</span>
                  {s.pinLocked ? (
                    <span className="kw-chip" title="Five wrong PINs. It unlocks by itself after a while, or give a new PIN now.">
                      PIN locked
                    </span>
                  ) : (
                    <span className={`kw-chip ${s.hasPin ? "" : "is-review"}`}>{s.hasPin ? "PIN set" : "No PIN yet"}</span>
                  )}
                </span>
              ),
            },
            {
              key: "round",
              header: t("Usual round"),
              cell: (s) => (
                <span className="kw-chips ksv-round">
                  <span className={s.round?.set ? "" : "kw-muted"}>{roundSummary(s.round, t)}</span>
                  <button type="button" className="ksv-link" aria-label={t("Edit {{name}}'s usual round", { name: s.name })} onClick={() => setRoundFor(s)}>
                    {t("Edit")}
                  </button>
                </span>
              ),
            },
            { key: "worker", header: t("Worker"), cell: (s) => workerCell(s, true) },
          ]}
        />
      )}

      {isAdmin ? (
        <section className="ksv-managers" aria-labelledby="ksv-managers-title">
          <h2 id="ksv-managers-title" className="ksv-sub">
            {t("Managers")}
          </h2>
          <p className="kw-muted">{t("A manager who also works here can be paid as a worker. Someone else marks their attendance and enters their pay.")}</p>
          {managers === "error" ? (
            <div className="kw-empty">
              {t("Could not load managers")}{" "}
              <button type="button" className="ksv-link" onClick={loadManagers}>
                {t("Try again")}
              </button>
            </div>
          ) : (
            <GeoResponsiveList
              rows={managers || []}
              rowKey={(s) => s.id}
              tableClassName="ksv-table"
              empty={managers === null ? t("Loading…") : t("No managers in this studio")}
              actions={(s) =>
                !s.worker ? (
                  <Button size="sm" outline onClick={() => alsoWorks(s)}>
                    {t("Also works here")}
                  </Button>
                ) : null
              }
              columns={[
                { key: "name", header: t("Name"), cell: (s) => s.name },
                { key: "phone", header: t("Phone"), cell: (s) => (s.phoneLast4 ? <span className="kw-code">…{s.phoneLast4}</span> : "—") },
                { key: "worker", header: t("Worker"), cell: (s) => workerCell(s, false) },
              ]}
            />
          )}
        </section>
      ) : null}

      <Modal isOpen={open} toggle={() => setOpen(false)} modalClassName="kw-modal">
        <ModalHeader toggle={() => setOpen(false)}>Add supervisor</ModalHeader>
        <ModalBody>
          <Label for="ks-name">Name</Label>
          <Input id="ks-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
          <Label className="mt-2" for="ks-phone">
            Mobile number (they log in with this)
          </Label>
          <Input
            id="ks-phone"
            type="tel"
            inputMode="numeric"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="10 digits"
          />
          <Label className="mt-2" for="ks-email">
            Email (optional)
          </Label>
          <Input id="ks-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Label className="mt-2" for="ks-pin">
            PIN (6 digits)
          </Label>
          <div className="kw-toolbar" style={{ margin: 0 }}>
            <Input
              id="ks-pin"
              className="kw-code"
              inputMode="numeric"
              maxLength={6}
              value={form.pin}
              onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 6) })}
              autoComplete="off"
              style={{ flex: "1 1 160px" }}
            />
            <Button size="sm" outline onClick={() => setForm({ ...form, pin: makePin() })}>
              Make another
            </Button>
          </div>
          <p className="kw-muted mt-2" style={{ marginBottom: 0 }}>
            They sign in with their mobile number and this PIN, then stay signed in on that phone. Five wrong tries lock the PIN for a while (longer each time); giving a new PIN unlocks it at once.
            Send it straight to them; HKP doesn't send it anywhere.
          </p>
          <Label check className="mt-3 d-flex" style={{ gap: 8, alignItems: "flex-start" }}>
            <Input type="checkbox" checked={form.alsoWorker} onChange={(e) => setForm({ ...form, alsoWorker: e.target.checked })} style={{ position: "static", margin: "4px 0 0" }} />
            <span>
              Also works here and gets paid (office, by the hour)
              <span className="kw-muted d-block">They show in attendance and the pay sheet. Another supervisor or you mark their attendance.</span>
            </span>
          </Label>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button className="ResetBtn" disabled={busy || !form.name.trim() || !form.phone.trim() || form.pin.length !== 6} onClick={add}>
            {busy ? "Adding…" : "Add supervisor"}
          </Button>
        </ModalFooter>
      </Modal>

      {editing ? <SupervisorEditSheet supervisor={editing} onClose={() => setEditing(null)} onSaved={saved} onRefresh={load} /> : null}
      {mapping ? <SupervisorMapSheet supervisor={mapping} onClose={() => setMapping(null)} onSaved={saved} /> : null}
      {roundFor ? <SupervisorRoundSheet supervisor={roundFor} onClose={() => setRoundFor(null)} onSaved={saved} /> : null}

      <Modal isOpen={!!shown} toggle={() => setShown(null)} modalClassName="kw-modal">
        <ModalHeader toggle={() => setShown(null)}>Send {shown?.name} their login</ModalHeader>
        {shown ? (
          <ModalBody>
            <p className="kw-muted" style={{ marginTop: 0 }}>
              {shown.pin
                ? "The PIN is shown only now. Send it straight to them:"
                : "Their link and mobile number (the PIN isn't stored readable; use New PIN if they've lost it):"}
            </p>
            <pre className="ksv-msg">{loginMessage({ name: shown.name, studio, url: loginUrl, phone: shown.phone, pin: shown.pin })}</pre>
            <div className="ksv-share">
              <a className="kh-btn is-primary" href={whatsappHref(shown.phone, loginMessage({ name: shown.name, studio, url: loginUrl, phone: shown.phone, pin: shown.pin }))} target="_blank" rel="noreferrer">
                WhatsApp
              </a>
              <a className="kh-btn" href={smsHref(shown.phone, loginMessage({ name: shown.name, studio, url: loginUrl, phone: shown.phone, pin: shown.pin }))}>
                SMS
              </a>
              <button
                type="button"
                className="kh-btn"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(loginMessage({ name: shown.name, studio, url: loginUrl, phone: shown.phone, pin: shown.pin }));
                    notifications({ title: "Copied", message: "Message copied — paste it anywhere", type: "success" });
                  } catch {
                    notifications({ title: "Copy failed", message: "Select the message and copy it", type: "warning" });
                  }
                }}
              >
                Copy
              </button>
              {typeof navigator !== "undefined" && (navigator as any).share ? (
                <button
                  type="button"
                  className="kh-btn"
                  onClick={() =>
                    (navigator as any)
                      .share({ title: "HKP Karkhana login", text: loginMessage({ name: shown.name, studio, url: loginUrl, phone: shown.phone, pin: shown.pin }) })
                      .catch(() => undefined)
                  }
                >
                  Share…
                </button>
              ) : null}
            </div>
          </ModalBody>
        ) : null}
        <ModalFooter>
          <Button className="ResetBtn" onClick={() => setShown(null)}>
            Done
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default KarkhanaSupervisorsPage;
